/**
 * Rank Ladder — "The Climb" (specs/rank-ladder): the "rank journey screen"
 * deferred from specs/xp-and-ranks. A tall mountain scene the player scrolls,
 * with all 10 ranks as camps along a trail (Icon summit → Rookie basecamp).
 * Geometry (slots, trail, mountain paths) is lifted from the design variation 1a
 * (SummitClimbScreen.js) so badges sit on the hand-drawn trail. Fixed dark palette,
 * deliberately not theme-derived, matching the XP celebration surfaces.
 */

export const RANK_LADDER_TEST_IDS = {
  SCREEN: 'rank-ladder-screen',
  BACK: 'rank-ladder-back',
  SCROLL: 'rank-ladder-scroll',
  /** Suffixed with the rank index, e.g. `rank-ladder-row-7`. */
  ROW: 'rank-ladder-row',
  CURRENT_ROW: 'rank-ladder-current-row',
  NEXT_MARKER: 'rank-ladder-next-marker',
  XP_BAR: 'rank-ladder-xp-bar',
  YOU_MARKER: 'rank-ladder-you-marker',
  SUMMIT: 'rank-ladder-summit',
  UNLOCK_PILL: 'rank-ladder-unlock-pill',
  BASECAMP: 'rank-ladder-basecamp',
} as const;

/** Design canvas: badges are authored against a ~402pt-wide, 1640pt-tall scene. */
export const SCENE_WIDTH = 402;
export const SCENE_HEIGHT = 1640;

/**
 * Fixed slot per rank index (1 = Rookie basecamp … 10 = Icon summit). `top`/`x` are
 * the badge **centre** in scene px; `baseSize` is its non-camp render size. Positions are
 * index-fixed (never distance-ramped) so badges stay on the trail for any current rank.
 *
 * Camps 1–9 form a symmetric switchback: evenly spaced `CAMP_SPACING` apart, alternating
 * between `CAMP_X_LEFT` / `CAMP_X_RIGHT` — a mirrored pair about the scene's centre line
 * (x = 201). `TRAIL_PATH` turns `TRAIL_INSET` px inboard of each camp, so every camp sits
 * the *same* distance off the trail on its own side. The design's hand-placed coordinates
 * were lopsided (camps ran 129px off the trail on the left but 12px on the right), which
 * read as badges scattered around the line rather than camps pinned to it.
 */
export interface RankSlot {
  top: number;
  x: number;
  baseSize: number;
}

/** The two camp columns, mirrored about the scene's centre line (x = SCENE_WIDTH / 2). */
export const CAMP_X_LEFT = 118;
export const CAMP_X_RIGHT = SCENE_WIDTH - CAMP_X_LEFT; // 284
/** Even vertical gap between camps 1–9. */
export const CAMP_SPACING = 146;
/** How far the trail's switchback turns inboard of each camp (= every camp's gap to it). */
export const TRAIL_INSET = 50;

export const SCENE_SLOTS: Record<number, RankSlot> = {
  // Icon is the prize above the climb: it clears the snow tip (apex y = 150) rather than
  // sitting embedded in it, so its label reads over sky instead of over the white cap.
  10: { top: 83, x: 201, baseSize: 78 },
  9: { top: 310, x: CAMP_X_LEFT, baseSize: 46 },
  8: { top: 456, x: CAMP_X_RIGHT, baseSize: 48 },
  7: { top: 602, x: CAMP_X_LEFT, baseSize: 50 },
  6: { top: 748, x: CAMP_X_RIGHT, baseSize: 52 },
  5: { top: 894, x: CAMP_X_LEFT, baseSize: 56 },
  4: { top: 1040, x: CAMP_X_RIGHT, baseSize: 58 },
  3: { top: 1186, x: CAMP_X_LEFT, baseSize: 58 },
  2: { top: 1332, x: CAMP_X_RIGHT, baseSize: 46 },
  1: { top: 1478, x: CAMP_X_LEFT, baseSize: 44 },
};

/**
 * Top of the BASECAMP label, in the Rookie column below its camp. Clears the *tallest*
 * bottom stack — Rookie as the current camp, whose badge grows to CAMP_SIZE and carries a
 * rank line, XP bar and XP label down to ~1575 — not just the small badge + name it sits
 * under the rest of the time. It used to sit at 1540 and collided with the Rookie label.
 */
export const BASECAMP_LABEL_TOP = 1592;

/**
 * The sticky header floats over the scene with no backing, and an Icon player auto-scrolls
 * to y = 0 (the target clamps) — so without headroom the summit camp renders under "THE
 * CLIMB". The scene is pushed down by the header block (safe area + title) plus a gap.
 * The block is 58 tall below the safe area (8 pad + 40 bar + 10 pad), so this leaves 12.
 */
export const SCENE_HEADER_CLEARANCE = 70;

export const getSceneHeadroom = (safeAreaTop: number): number =>
  safeAreaTop + SCENE_HEADER_CLEARANCE;

/** Enlarged badge for the current camp; small bump for the next camp. */
export const CAMP_SIZE = 92;
export const NEXT_BUMP = 6;

/** Gentle float loop (px, ms) on the current + summit badges. */
export const FLOAT_DISTANCE = 6;
export const FLOAT_DURATION = 3300;

/** Fraction of the viewport used as the target centre for auto-scroll (0.5 = middle). */
export const SCROLL_CENTER_RATIO = 0.5;

/** Above-camp badges fade toward the peak; opacity ramps down with distance. */
export const ABOVE_OPACITY_NEAR = 0.85;
export const ABOVE_OPACITY_FAR = 0.5;

/* ---- Scenery (baked at SCENE_HEIGHT = 1640) ---- */

export const SKY_STOPS = ['#1A0E38', '#241150', '#3A1E6E', '#2A1A4A', '#140C28'] as const;
export const SKY_LOCATIONS = [0, 0.16, 0.4, 0.64, 1] as const;

export const MOUNTAIN_PATH = 'M0 1640 L0 620 L120 360 L201 150 L285 380 L402 640 L402 1640 Z';
export const SNOW_PATH = 'M201 150 L160 255 L201 235 L242 300 L285 380 L201 150 Z';
export const RIDGE_PATH = 'M0 1640 L0 980 L90 820 L210 1010 L402 900 L402 1640 Z';
/**
 * The switchback the camps flank. Each cubic runs between two turning points — one per
 * camp, `TRAIL_INSET` inboard of it (x = 168 / 234) at exactly the camp's `top` — with its
 * control points stacked vertically on the turn, giving a vertical tangent there. That is
 * what holds every camp the same 50px off the line; re-deriving one without the other
 * re-introduces the lopsided scatter this replaced. Final leg bends to the summit (201).
 */
export const TRAIL_PATH =
  'M168 1478 C 168 1405, 234 1405, 234 1332 C 234 1259, 168 1259, 168 1186 ' +
  'C 168 1113, 234 1113, 234 1040 C 234 967, 168 967, 168 894 ' +
  'C 168 821, 234 821, 234 748 C 234 675, 168 675, 168 602 ' +
  'C 168 529, 234 529, 234 456 C 234 383, 168 383, 168 310 ' +
  'C 168 275, 201 275, 201 240';

export const RANK_LADDER_COLORS = {
  BACKGROUND: '#140C28',
  HEADER_TITLE: '#C4B5FD',
  BACK_ICON: '#C4B5FD',
  // scenery
  MOUNTAIN_TOP: '#4C2E8A',
  MOUNTAIN_BOTTOM: '#1B1030',
  SNOW_TOP: '#EDE9FE',
  SNOW_BOTTOM: '#C4B5FD',
  RIDGE: '#160C2C',
  TRAIL: '#F9FAFB',
  // summit / camp
  SUMMIT_LABEL: '#FCD34D',
  GOAT_GLOW: 'rgba(252,211,77,0.22)',
  YOU_GLOW: 'rgba(252,211,77,0.28)',
  PIN_BG: '#FCD34D',
  PIN_TEXT: '#3A2600',
  YOU_RANK: '#FCD34D',
  // next / unlock
  NEXT_RING: 'rgba(167,139,250,0.6)',
  NEXT_NAME: '#C4B5FD',
  UNLOCK_BG: 'rgba(167,139,250,0.16)',
  UNLOCK_TEXT: '#A78BFA',
  // xp bar
  XP_TRACK: 'rgba(255,255,255,0.14)',
  XP_FILL_FROM: '#A78BFA',
  XP_FILL_TO: '#6EE7B7',
  XP_LABEL: '#C4B5FD',
  // above (locked) / below (cleared) labels
  ABOVE_LABEL: '#9C94B2',
  BELOW_LABEL: '#CBD5E1',
  BASECAMP_LABEL: '#6B7280',
} as const;
